import { generateTimeBasedKey } from '../common';
import { ClientAttributesSelectorModel } from '@/app/core/components/client-attributes';
import {
  ProgramTransformer,
  ProgramTransformerArgs,
} from '@/app/core/components/program-wizard';
import { getCurrentFeatureCode, OktaUserInfo } from '@/app/core/auth';
import {
  AppUserProfile,
  AscendArchivesProgram,
  DefaultPinningThresholdValue,
  DefaultValidationThresholdValue,
  FieldDelimiter,
} from '@ssar-app/domain';

export const removeOrderNameForCloneProgramFlow: ProgramTransformer = async (
  args: ProgramTransformerArgs,
) => {
  const { program, programWizardMetadata } = args;
  const newProgram = program as AscendArchivesProgram;
  if (
    programWizardMetadata.isClone &&
    newProgram &&
    newProgram.productInput?.productDetails
  ) {
    newProgram.productInput.productDetails.programName = '';
  }
  return Promise.resolve({
    ...args,
    program,
  });
};

export const prepareProgramAndDependentData: ProgramTransformer = async (
  args: ProgramTransformerArgs,
) => {
  const {
    program,
    programWizardMetadata,
    programWizardDependentData,
    messagesStore,
  } = args;
  // prepare the program
  const userProfile = OktaUserInfo.getOktaUserInfo();
  const productName = getCurrentFeatureCode();
  const actualProgram = prepareProgram(
    program as AscendArchivesProgram,
    userProfile,
    programWizardMetadata?.isClone,
    programWizardDependentData.clientAttributesModel,
    productName || '',
  );

  return {
    ...args,
    program: {
      ...actualProgram,
    },
    messagesStore: messagesStore,
    programWizardDependentData: {
      ...programWizardDependentData,
    },
  };
};

const prepareProgram = (
  program: AscendArchivesProgram,
  userProfile: AppUserProfile,
  isClone: boolean,
  clientAttributesModel: ClientAttributesSelectorModel,
  productName: string,
): AscendArchivesProgram => {
  const { productInput, packagesInput, ...restProgram } = program || {};
  const { productDetails, ...restProductInput } = productInput || {};
  const { frontEndProcessing, ...restPackagesInput } = packagesInput || {};
  const { thresholds } = frontEndProcessing || {};
  const {
    accountNumber: _accountNumber,
    subCode: _subCode,
    submitterName: _submitterName,
    submitterEmail: _submitterEmail,
    ...restProductDetails
  } = productDetails || {};
  const {
    inputDelim: _inputDelim,
    inputFormat: _inputFormat,
    ...restFrontEndProcessing
  } = frontEndProcessing || {};
  const {
    validationThreshold: _validationThreshold,
    pinningThreshold: _pinningThreshold,
    ...restThresholds
  } = thresholds || {};

  const newProgram: AscendArchivesProgram = {
    ...restProgram,
    metadata: {
      name: productName,
      version: 'v1',
    },
    productInput: {
      ...restProductInput,
      productDetails: {
        accountNumber:
          clientAttributesModel?.clientAttributes?.details?.accountNumber || '',
        subCode: clientAttributesModel?.subscriberCode || '',
        submitterName: userProfile.firstName + ' ' + userProfile.lastName,
        submitterEmail: userProfile.email || '',
        ...restProductDetails,
      },
    },
    packagesInput: {
      ...restPackagesInput,
      frontEndProcessing: {
        ...restFrontEndProcessing,
        thresholds: {
          validationThreshold: DefaultValidationThresholdValue,
          pinningThreshold: DefaultPinningThresholdValue,
          ...restThresholds,
        },
        inputDelim: FieldDelimiter.COMMA,
        inputFormat: 'CSV',
      },
    },
  };
  if (isClone) {
    newProgram.metadata.clonedFromProgram = {
      programKey: program.programKey,
      orderName: program.productInput.productDetails.programName,
      fileName: program.packagesInput.frontEndProcessing.experianInputFileName,
    };
  }
  newProgram.subCode = clientAttributesModel?.subscriberCode || '';
  newProgram.programKey = generateTimeBasedKey();
  return newProgram;
};
